import React from 'react'

import { addToCheckoutCart, initBedbankSession, initialiseCheckout } from 'actions/CheckoutActions'
import { fetchPropertyRates, updatePropertyPricingData } from 'actions/PropertyActions'
import { addToUserCart } from 'actions/UserCartActions'
import * as Analytics from 'analytics/analytics'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import { FlightViewTypes } from 'constants/flight'
import type { ModalContextType } from 'contexts/ModalContext'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import type {
  OfferPageDispatchAction,
  OfferPageRoomOption,
} from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import {
  generateBedbankPropertyAccommodationCheckoutItems,
  generateLpcPropertyAccommodationCheckoutItems,
} from 'lib/checkout/accommodation/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { floatify } from 'lib/maths/mathUtils'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import type { AppDispatch } from 'src/client/store'

interface ChangedRate {
  rate: App.BedbankPropertyRate
  newPrice?: number
  newLuxPlusPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

function calculatePriceChange(ch: ChangedRate, isLuxPlusBooking: boolean): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = (isLuxPlusBooking && !!ch.rate.luxPlusTotal) ?
    ch.rate.luxPlusTotal.sellingPrice :
    ch.rate.total.sellingPrice
  const newLuxPlusPrice = ch.newLuxPlusPrice ?? ch.rate.luxPlusTotal?.sellingPrice ?? 0
  const newDisplayPrice = (isLuxPlusBooking && newLuxPlusPrice) ?
    newLuxPlusPrice :
    ch.newPrice ?? ch.rate.total.sellingPrice
  // NB: We're going to _assume_ the property fees are equivalent between standard and LuxPlus+
  // pricing.
  const oldPropertyFees = ch.rate.total.propertyFees
  const newPropertyFees = ch.newPropertyFees ?? oldPropertyFees
  return {
    oldDisplayPrice,
    newDisplayPrice,
    oldPropertyFees,
    newPropertyFees,
  }
}

/**
 * Dispatches an action to patch the changed rate in the offer store and returns a semi-patched rate
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRate(dispatch: AppDispatch, {
  propertyId,
  checkIn,
  checkOut,
  utmContent,
  roomTypeId,
  roomRateId,
  changedRate: { rate, newPrice, newLuxPlusPrice, newPropertyFees, rooms },
}: {
  propertyId: string
  checkIn: string
  checkOut: string
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  changedRate: ChangedRate
}): App.BedbankPropertyRate {
  const resolvedPrice = newPrice ?? rate.total.sellingPrice
  const resolvedLuxPlusPrice = newLuxPlusPrice ?? rate.luxPlusTotal?.sellingPrice
  // NB: We're going to _assume_ the property fees are equivalent between standard and LuxPlus+
  // pricing.
  const resolvedPropertyFees = newPropertyFees ?? rate.total.propertyFees

  dispatch(updatePropertyPricingData(propertyId, {
    checkIn,
    checkOut,
    occupancies: rooms,
    utmContent,
    roomTypeId,
    roomRateId,
    roomId: rate.roomId,
    rateId: rate.rateId,
    newPrice: resolvedPrice,
    newLuxPlusPrice: resolvedLuxPlusPrice,
    newPropertyFees: resolvedPropertyFees,
  }))

  // We don't need to be as thorough here; we only need to patch the fields that're used to perform
  // a bedbank service enquiry request.
  return {
    ...rate,
    total: {
      ...rate.total,
      sellingPrice: resolvedPrice,
      propertyFees: resolvedPropertyFees,
    },
    ...(resolvedLuxPlusPrice && {
      luxPlusTotal: {
        ...(rate.luxPlusTotal ?? rate.total),
        sellingPrice: resolvedLuxPlusPrice,
        propertyFees: resolvedPropertyFees,
      }
    })
  }
}

interface LpcOfferAndRate {
  type: 'lpc'
  offer: App.HotelOffer
  rate: App.LpcPropertyRate
}

interface BedbankOfferAndRate {
  type: 'bedbank'
  offer: App.BedbankOffer
  rate: App.BedbankPropertyRate
}

export type OfferAndRate = LpcOfferAndRate | BedbankOfferAndRate

interface SingleRoomArgs {
  shouldAddToCart: boolean
  dispatch: AppDispatch
  offerPageDispatch: React.Dispatch<OfferPageDispatchAction>
  modalElementContext: ModalElementContextValue<never>
  showModal: ModalContextType
  propertyId: string
  offerAndRate: OfferAndRate
  optionNumber: number
  originAirportCode: string | undefined
  destinationAirportCode: string | undefined
  checkIn: string
  checkOut: string
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  currency: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
  roomListId: string
  roomPosition: number
  rateListId: string
}

export async function propertySingleRoomBookNowOrAddToCartCallback(args: SingleRoomArgs) {
  const {
    shouldAddToCart,
    dispatch,
    offerPageDispatch,
    modalElementContext,
    showModal,
    propertyId,
    offerAndRate,
    optionNumber,
    originAirportCode,
    destinationAirportCode,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    currency,
    source,
    utmContent,
    roomOption,
    isSpoofed,
    subscriber,
    roomListId,
    roomPosition,
    rateListId,
  } = args
  const { type, offer, rate } = offerAndRate
  const period = { checkIn, checkOut, duration }
  const accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem> = []
  if (type === 'lpc') {
    accommodationItems.push(...generateLpcPropertyAccommodationCheckoutItems(
      rate,
      period,
      rooms,
    ))
  } else if (type === 'bedbank') {
    let sessionId: string | undefined
    if (!shouldAddToCart) {
      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal message="Please wait..." />
      })
      try {
        // TODO(HP-1329): Preserve original hotel-only prices so we needn't jump through these hoops.
        const cheapestFlightPrice = typeof rate.cheapestFlightPrice === 'number' ? rate.cheapestFlightPrice : 0
        const session = await initBedbankSession(dispatch, {
          propertyId: rate.bedbankPropertyId,
          roomTypeId: rate.bedbankRoomId,
          roomRateId: rate.bedbankRateId,
          rateGroupId: rate.rateGroupId,
          bedGroupId: rate.bedGroups[0].id,
          isFlightBundle: rate.isFlightBundle,
          csBooking: isSpoofed,
          region: regionCode,
          rooms,
          checkIn,
          checkOut,
          price: floatify(rate.total.sellingPrice - cheapestFlightPrice),
          propertyFees: rate.total.propertyFees,
          memberInclusive: rate.luxPlusTotal?.sellingPrice ? floatify(rate.luxPlusTotal.sellingPrice - cheapestFlightPrice) : undefined,
          subscriber,
          source,
        })
        closeProcessingModal()
        if (!('sessionId' in session)) {
          const changedRate = {
            rate,
            newPrice: session.price,
            newLuxPlusPrice: session.luxplusPrice,
            newPropertyFees: session.propertyFees,
            rooms,
          }
          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRate, !!subscriber?.tier)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRate = updateChangedRate(dispatch, {
              propertyId,
              checkIn,
              checkOut,
              utmContent,
              roomTypeId: roomOption?.roomTypeId,
              roomRateId: roomOption?.roomRateId,
              changedRate,
            })
            return propertySingleRoomBookNowOrAddToCartCallback({
              ...args,
              offerAndRate: { ...offerAndRate, rate: newRate }
            })
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          dispatch(fetchPropertyRates(propertyId, {
            checkIn,
            checkOut,
            occupancies: rooms,
            utmContent,
            roomTypeId: roomOption?.roomTypeId,
            roomRateId: roomOption?.roomRateId,
            refresh: true,
          }))
          return
        }
        sessionId = session.sessionId
      } catch {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        dispatch(fetchPropertyRates(propertyId, {
          checkIn,
          checkOut,
          occupancies: rooms,
          utmContent,
          roomTypeId: roomOption?.roomTypeId,
          roomRateId: roomOption?.roomRateId,
          refresh: true,
        }))
        showModal(<BedbankSoldOutModal />)
        return
      }
    }

    accommodationItems.push(...generateBedbankPropertyAccommodationCheckoutItems(
      sessionId,
      rate,
      period,
      rooms,
    ))
  }

  const flightItems: Array<App.Checkout.FlightItem> = []
  if (rate.includesFlights) {
    if (!(originAirportCode && typeof rate.cheapestFlightPrice === 'number')) {
      throw new Error('Attempted to perform cart action on rate with flights when no flight pricing is available')
    }
    const flightOccupants = countOccupantsForFlights(rooms)
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode,
      destinationAirportCode,
      occupants: flightOccupants,
      passengers: generatePassengersFromOccupants(flightOccupants),
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      bundledItemIds: accommodationItems.map(({ itemId }) => itemId),
      quotedFare: rate.cheapestFlightPrice / rooms.length,
    }))
  }

  const checkoutItems = [...accommodationItems, ...flightItems]

  offerPageDispatch({ type: OfferPageActions.closeFilter })
  modalElementContext?.resolve()

  if (flightItems.length) {
    dispatch(initialiseCheckout(checkoutItems, { mode: 'vertical-select' }))
  } else if (!shouldAddToCart) {
    dispatch(addToCheckoutCart(checkoutItems))
  }

  // TODO(Xiao): Confirm this is the price that Data want.
  fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.luxPlusTotal?.displayPrice ?? rate.total.displayPrice, sumUpOccupancies(rooms), 'hotel'))
  // TODO(Xiao): Figure out why the other rate types don't fire this event for flights.
  // NB: This event doesn't appear to fire immediately; it seems to be waiting on
  // `checkoutStateIsReady()`.
  Analytics.trackEvent(addToCart({
    roomListId,
    roomPosition,
    rateListId,
    ratePosition: optionNumber,
    type: 'property',
    rate,
    currency,
  }))

  if (flightItems.length) {
    dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    return
  }

  if (shouldAddToCart) {
    dispatch(addToUserCart(checkoutItems))
    return
  }

  dispatch(navigateToCheckout())
}
